<template>
	<MStateBox :loading="initialLoading" :empty="emptyData" class="report-wrap">
		<CTabs
			v-if="tabOption.length > 1"
			v-model="activeTab"
			:option="tabOption"
			:ellipsis="false"
			@change="changeActive"
		/>
		<div class="scroll-wrap" v-for="(t, index) in tabOption" :key="index" v-show="activeTab == index">
			<CScrollPro
				ref="scrollRef"
				pullDown
				@pull-down="scrollPullDown"
				pullUp
				:moreData="listData[index].hasMore"
				@pull-up="scrollPullUp"
			>
				<div class="item-wrap" v-if="listData[index].list.length">
					<CCardReportPanel
						v-for="(item, idx) in listData[index].list"
						:key="idx"
						:item="item"
						:border="idx != listData[index].list.length - 1"
					></CCardReportPanel>
				</div>
				<div class="c-visible-content" v-if="!listData[index].emptyShow && !listData[index].hasMore">
					<span class="c-visible-content__text">{{ tipsText[i18n.locale] }}</span>
				</div>
				<div v-if="listData[index].emptyShow" class="empty-box">
					<CFullEmpty :emptyText="$t('enterprise.no-report-data')" />
					<div class="c-visible-content">
						<span class="c-visible-content__text">{{ tipsText[i18n.locale] }}</span>
					</div>
				</div>
			</CScrollPro>
		</div>
	</MStateBox>
</template>

<script setup>
import { ref, computed, onBeforeMount, getCurrentInstance, nextTick } from 'vue';
import CTabs from '@/view/enterprise/components/c-tabs.vue';
import CScrollPro from '@/view/enterprise/components/c-scroll-pro.vue';
import CCardReportPanel from '@/view/enterprise_rebuild/materials/shared/report/CardReportPannel.vue';
import CFullEmpty from '@/view/enterprise/components/c-full-empty.vue';
import MStateBox from '@/view/enterprise_rebuild/components/MStateBox.vue';
import { useRoute } from '@/utils/vue-api';
import i18n from '@/assets/js/i18n';
import { useModuleData } from '@/view/enterprise_rebuild/hooks/data/useModuleData';

const tipsText = {
	'zh-cn': '*进门提供数据以及技术支持，仅供交流参考使用，不构成任何投资建议',
	'zh-hk': '*進門提供數據以及技術支持，僅供交流參考使用，不構成任何投資建議',
	'en': '*Technical support by Finter. For reference only; not intended as investment advice'
};

// 获取当前实例以使用 proxy.$set
const { proxy } = getCurrentInstance();

const { report_tabs, report_list } = useModuleData();
const route = useRoute();
const scrollRef = ref(null);

const emptyData = ref(false);
const tabOption = ref([]);
const REPORT_RATE = ref([]);
const activeTab = ref(0);
const listData = ref([
	{
		list: [],
		pagestart: 0,
		hasMore: true,
		rating: '',
		emptyShow: false
	}
]);

const initialLoading = ref(true);

async function initializeData() {
	// 使用 useModuleData 获取标签页配置
	await report_tabs.refresh();

	// 检查是否有数据或错误
	if (report_tabs.error || !report_tabs.data?.data) {
		emptyData.value = true;
		return;
	}

	tabOption.value = (report_tabs.data?.data || [])
		.sort((a, b) => a.sort - b.sort)
		.filter(v => v.isPublish)
		.map(v => {
			//1:买入 3:中性 4:未评级 2:卖出
			REPORT_RATE.value.push(v);
			return v.name;
		});

	const { active } = route.query;
	if (active) {
		const findTabIndex = REPORT_RATE.value.findIndex(v => v.value == active);
		if (findTabIndex != -1) {
			activeTab.value = findTabIndex;
		}
	}

	initList();
	loadData();
}

function changeActive(idx) {
	if (report_list.loading) return;
	activeTab.value = idx;

	// 重新初始化滚动组件
	//scrollRef.value[activeTab.value] && scrollRef.value[activeTab.value].init();

	if (listData.value[idx].list.length == 0 && listData.value[idx].hasMore) {
		loadData(true);
	}
}

function initList() {
	if (tabOption.value.length) {
		const newListData = REPORT_RATE.value.map((item, index) => {
			const currId = item.value;
			const currTabIds = [currId, ...(item.subTabList || []).map(sub => sub.value)].map(v => Number(v));
			return {
				list: [],
				pagestart: 0,
				hasMore: true,
				rating: !currId ? '' : currTabIds,
				emptyShow: false
			};
		});
		// 使用 proxy.$set 更新整个数组
		proxy.$set(listData, 'value', newListData);
	} else {
		// 没有Tab时，就请求全部数据
		const newListData = [
			{
				list: [],
				pagestart: 0,
				hasMore: true,
				rating: '',
				emptyShow: false
			}
		];
		// 使用 proxy.$set 更新整个数组
		proxy.$set(listData, 'value', newListData);
	}
}

async function loadData(reload = false) {
	let target = listData.value[activeTab.value];

	// 确保 target 存在
	if (!target) {
		console.warn('Target data not found for activeTab:', activeTab.value);
		return;
	}

	await report_list.refresh({
		pagestart: target.pagestart,
		pagenum: 20,
		rating: target.rating,
		sourceData: 1
	});

	// 数据访问路径：根据历史组件，response?.rows
	const result = report_list.data?.rows || [];
	const total = report_list.data?.total || 0;

	// 创建新的对象来确保响应式更新
	const updatedTarget = {
		...target,
		list: reload ? result : [...target.list, ...result],
		hasMore: (reload ? result.length : target.list.length + result.length) < total,
		emptyShow: !total
	};

	// 使用 proxy.$set 强制触发响应式更新
	proxy.$set(listData.value, activeTab.value, updatedTarget);

	// 确保响应式更新
	await nextTick();
}

async function scrollPullDown(done) {
	setPageStart('pullDown');
	await loadData(true);
	done();
}

async function scrollPullUp(done) {
	if (report_list.loading) return;
	setPageStart('pullUp');
	await loadData();
	done();
}

function setPageStart(type) {
	let _activeTab = activeTab.value;
	let target = listData.value[_activeTab];

	// 确保 target 存在
	if (!target) {
		console.warn('Target data not found for activeTab:', _activeTab);
		return;
	}

	// 创建新的对象来确保响应式更新
	const updatedTarget = {
		...target,
		pagestart: type == 'pullDown' ? 0 : target.pagestart + 1
	};

	// 使用 proxy.$set 强制触发响应式更新
	proxy.$set(listData.value, _activeTab, updatedTarget);
}

onBeforeMount(async () => {
	await initializeData();
	initialLoading.value = false;
});
</script>

<style lang="less" scoped>
.report-wrap {
	height: 100%;
	background: #fff;
	/deep/.module-state-content {
		display: flex;
		flex-direction: column;
	}
	.scroll-wrap {
		flex: 1;
		height: 0;
	}
	.item-wrap {
		padding: 0.14rem 0.14rem 0;
	}
	.empty-box {
		height: calc(100vh - 0.4rem);
		display: flex;
		flex-direction: column;
		gap: 32px;
	}
}

.c-visible-content {
	padding: 0.14rem;
	&__text {
		color: #c5c5c5;
		/* 12/B1-regular */
		font-family: 'PingFang SC';
		font-size: 0.12rem;
		font-style: normal;
		font-weight: 400;
		line-height: 140%; /* 16.8px */
	}
}
</style>
